import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Upload, Search, Pencil, Trash2, ExternalLink, FileText, FileImage,
  FileArchive, File as FileIcon, Link2, Loader2, Layers,
} from "lucide-react";
import {
  fetchLibrary, fetchLibraryCategories, fetchLibraryCollections,
  deleteLibraryDoc, openProtectedFile, fetchValidation,
} from "@/lib/api";
import { errorMessage } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import LibraryFormSheet from "@/components/LibraryFormSheet";
import MultiSelect from "@/components/MultiSelect";

const fmtSize = (n) => {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
};

// Pick an icon based on file extension.
function DocIcon({ ext, isLink, size = 14 }) {
  if (isLink) return <Link2 size={size} className="text-[#3A2A10]" />;
  const e = (ext || "").toLowerCase();
  if (["jpg", "jpeg", "png", "gif", "webp", "heic", "heif"].includes(e)) {
    return <FileImage size={size} className="text-[#3A2A10]" />;
  }
  if (["zip"].includes(e)) {
    return <FileArchive size={size} className="text-[#3A2A10]" />;
  }
  if (e === "pdf") {
    return <FileText size={size} className="text-[#3A2A10]" />;
  }
  return <FileIcon size={size} className="text-[#3A2A10]" />;
}

export default function Library() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cats, setCats] = useState([]);
  const [selCats, setSelCats] = useState([]);
  const [mfg, setMfg] = useState("");
  const [cal, setCal] = useState("");
  const [selCollections, setSelCollections] = useState([]);
  const [allCollections, setAllCollections] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [validation, setValidation] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (selCats.length) params.category = selCats;
      if (mfg.trim()) params.manufacturer = mfg.trim();
      if (cal.trim()) params.caliber = cal.trim();
      if (selCollections.length) params.collection = selCollections;
      const res = await fetchLibrary(params);
      setRows(res.items || []);
    } catch (err) {
      toast.error(errorMessage(err, "Failed to load library"));
    } finally {
      setLoading(false);
    }
  }, [q, selCats, mfg, cal, selCollections]);

  const loadCollections = useCallback(() => {
    fetchLibraryCollections()
      .then((d) => setAllCollections(d.collections || []))
      .catch(() => setAllCollections([]));
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    fetchLibraryCategories().then((d) => setCats(d.categories || [])).catch(() => {});
    fetchValidation().then(setValidation).catch(() => {});
    loadCollections();
  }, [loadCollections]);

  const onDelete = async (row) => {
    if (!window.confirm(`Delete "${row.title}"? This cannot be undone.`)) return;
    try {
      await deleteLibraryDoc(row.id);
      toast.success("Document removed");
      load();
      loadCollections();
    } catch (err) {
      toast.error(errorMessage(err, "Delete failed"));
    }
  };

  const onOpen = async (row) => {
    if (row.url) {
      // Link entry — just open the URL in a new tab.
      window.open(row.url, "_blank", "noopener,noreferrer");
      return;
    }
    try {
      await openProtectedFile(row.storage_path, row.original_filename);
    } catch (err) {
      toast.error(errorMessage(err, "Could not open document"));
    }
  };

  const clearFilters = () => {
    setQ(""); setSelCats([]); setMfg(""); setCal(""); setSelCollections([]);
  };

  const activeFilterCount = useMemo(
    () => (q ? 1 : 0) + (selCats.length ? 1 : 0) + (mfg ? 1 : 0) + (cal ? 1 : 0) + (selCollections.length ? 1 : 0),
    [q, selCats, mfg, cal, selCollections],
  );

  const toggleCollection = (name) => {
    setSelCollections((cur) => cur.includes(name) ? cur.filter((x) => x !== name) : [...cur, name]);
  };

  const calibers = validation["Caliber"] || [];

  return (
    <div className="px-6 md:px-10 py-8 pb-20 max-w-[1600px]" data-testid="library-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#3A2A10] mb-2">Library</div>
          <h1 className="font-serif-display text-4xl text-[#0A0604]">Reference Library</h1>
          <p className="text-sm text-[#3A2A10] mt-1 max-w-2xl">
            PDFs, spec sheets, service manuals, and hand-picked links to
            how-tos and forum threads. Group items into Collections
            (workflow groupings) so "Balance Work" or "Shock System Revival"
            show everything relevant in one click.
          </p>
        </div>
        <Button
          onClick={() => { setEditing(null); setFormOpen(true); }}
          className="h-9 px-4 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0C10] text-xs uppercase tracking-wider rounded-sm"
          data-testid="library-upload-btn"
        >
          <Upload size={14} className="mr-2" /> Add to Library
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-6">
        {/* Collections sidebar */}
        <aside className="space-y-1" data-testid="library-collections-sidebar">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-[#3A2A10] mb-2 px-2">
            <Layers size={11} /> Collections
          </div>
          {allCollections.length === 0 ? (
            <div className="text-[10px] text-[#3A2A10]/70 italic px-2 py-1">
              Add collections when uploading to group items here.
            </div>
          ) : (
            allCollections.map((c) => (
              <button
                key={c.name}
                onClick={() => toggleCollection(c.name)}
                className={`w-full flex items-center justify-between text-left px-2 py-1.5 rounded-sm text-xs transition-colors ${
                  selCollections.includes(c.name)
                    ? "bg-[#D4AF37]/25 text-[#0A0604] font-medium"
                    : "text-[#3A2A10] hover:bg-[#2B2214]/[0.06]"
                }`}
                data-testid={`library-collection-${c.name}`}
              >
                <span className="truncate">{c.name}</span>
                <span className="font-mono-data text-[#3A2A10] text-[10px]">{c.count}</span>
              </button>
            ))
          )}
        </aside>

        <div className="min-w-0">

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_220px_200px_200px_auto] gap-3 mb-5">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#3A2A10]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search title, notes, filename…"
            className="w-full h-9 pl-9 pr-3 bg-[#F9F0D4] border border-[#8B6A1F]/35 rounded-sm text-sm text-[#0A0604] placeholder:text-[#3A2A10]/60 focus:outline-none focus:border-[#D4AF37]"
            data-testid="library-search"
          />
        </div>
        <MultiSelect
          value={selCats}
          onChange={setSelCats}
          options={cats}
          placeholder="All categories"
          testid="library-category-filter"
        />
        <input
          value={mfg}
          onChange={(e) => setMfg(e.target.value)}
          placeholder="Manufacturer…"
          className="h-9 px-3 bg-[#F9F0D4] border border-[#8B6A1F]/35 rounded-sm text-sm text-[#0A0604] placeholder:text-[#3A2A10]/60 focus:outline-none focus:border-[#D4AF37]"
          data-testid="library-mfg-filter"
        />
        <input
          value={cal}
          onChange={(e) => setCal(e.target.value)}
          placeholder="Caliber exact match…"
          list="library-caliber-options"
          className="h-9 px-3 bg-[#F9F0D4] border border-[#8B6A1F]/35 rounded-sm text-sm text-[#0A0604] placeholder:text-[#3A2A10]/60 focus:outline-none focus:border-[#D4AF37]"
          data-testid="library-caliber-filter"
        />
        <datalist id="library-caliber-options">
          {calibers.map((c) => <option key={c} value={c} />)}
        </datalist>
        {activeFilterCount > 0 && (
          <button
            onClick={clearFilters}
            className="h-9 px-3 text-[11px] uppercase tracking-wider text-[#3A2A10] hover:text-[#0A0604] border border-[#8B6A1F]/35 rounded-sm"
            data-testid="library-clear-filters"
          >
            Clear ({activeFilterCount})
          </button>
        )}
      </div>

      {/* Document list — card-style for readability. Each row is a 3-line
          stack: prominent title, meta line (category · mfg · size/link),
          and chip line (calibers + collections). Easier to scan than the
          old 6-column grid table. */}
      <div className="border border-[#8B6A1F]/35 rounded-sm bg-[#F9F0D4] overflow-hidden">
        {loading ? (
          <div className="px-5 py-12 text-sm text-[#3A2A10] flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Loading library…
          </div>
        ) : rows.length === 0 ? (
          <div className="px-4 py-16 text-center" data-testid="library-empty">
            <FileText size={24} className="mx-auto text-[#3A2A10] mb-3" strokeWidth={1.2} />
            <div className="font-serif-display text-lg text-[#0A0604] mb-1">
              {activeFilterCount > 0 ? "No matches" : "Your library is empty"}
            </div>
            <div className="text-xs text-[#3A2A10]">
              {activeFilterCount > 0
                ? "Try clearing the filters or broadening your search."
                : "Upload your first spec sheet, service manual, or BestFit catalog to get started."}
            </div>
          </div>
        ) : (
          <div className="divide-y divide-[#8B6A1F]/20">
            {rows.map((d) => (
              <div
                key={d.id}
                className="flex items-start gap-4 px-5 py-4 hover:bg-[#2B2214]/[0.04] transition-colors group"
                data-testid={`library-row-${d.id}`}
              >
                {/* Icon */}
                <div className="shrink-0 mt-0.5">
                  <DocIcon ext={d.ext} isLink={!!d.url} />
                </div>

                {/* Main content */}
                <div className="min-w-0 flex-1">
                  {/* Title — large, serif-display for a clear bench-side
                      read. No truncate so long titles wrap onto a 2nd line
                      instead of being chopped off mid-word. */}
                  <div
                    className="font-serif-display text-lg text-[#0A0604] leading-snug"
                    data-testid={`library-title-${d.id}`}
                  >
                    {d.title}
                  </div>

                  {/* Meta line — category badge + mfg + size/link host + date.
                      All in one row for compactness but with adequate weight
                      so they're scannable. */}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-[12px] text-[#3A2A10]">
                    {d.category && (
                      <span className="inline-flex items-center px-2 py-0.5 bg-[#2B2214]/[0.10] border border-[#8B6A1F]/35 rounded-sm text-[10px] uppercase tracking-wider">
                        {d.category}
                      </span>
                    )}
                    {d.manufacturer && (
                      <span className="font-mono-data text-[#0A0604]">{d.manufacturer}</span>
                    )}
                    {d.url ? (
                      <span className="font-mono-data truncate max-w-[260px]">
                        Link · {(() => { try { return new URL(d.url).hostname.replace(/^www\./, ""); } catch { return d.url; } })()}
                      </span>
                    ) : (
                      <span className="font-mono-data">{fmtSize(d.size)}</span>
                    )}
                    <span className="font-mono-data text-[#3A2A10]/70 ml-auto sm:ml-0">{fmtDate(d.created_at)}</span>
                  </div>

                  {/* Chips line — calibers (gold) + collections (muted) */}
                  {((d.calibers || []).length > 0 || (d.collections || []).length > 0) && (
                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                      {(d.calibers || []).slice(0, 6).map((c) => (
                        <span
                          key={`cal-${c}`}
                          className="text-[11px] px-1.5 py-0.5 bg-[#D4AF37]/20 text-[#0A0604] border border-[#D4AF37]/40 rounded-sm font-mono-data"
                          data-testid={`library-cal-${d.id}-${c}`}
                        >
                          {c}
                        </span>
                      ))}
                      {(d.calibers || []).length > 6 && (
                        <span className="text-[11px] text-[#3A2A10] font-mono-data">+{d.calibers.length - 6}</span>
                      )}
                      {(d.collections || []).map((c) => (
                        <span
                          key={`col-${c}`}
                          className="text-[11px] px-1.5 py-0.5 bg-[#2B2214]/[0.08] border border-[#8B6A1F]/30 rounded-sm text-[#3A2A10] inline-flex items-center gap-1"
                          data-testid={`library-col-${d.id}-${c}`}
                        >
                          <Layers size={10} /> {c}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Actions — right rail. Always visible (was hidden until
                    hover before, which is hostile on mobile/touch). */}
                <div className="shrink-0 flex items-center gap-1 sm:opacity-70 sm:group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => onOpen(d)}
                    className="p-2 text-[#3A2A10] hover:text-[#0A0604] hover:bg-[#2B2214]/[0.06] rounded-sm"
                    aria-label="Open"
                    title="Open"
                    data-testid={`library-open-${d.id}`}
                  >
                    <ExternalLink size={14} />
                  </button>
                  <button
                    onClick={() => { setEditing(d); setFormOpen(true); }}
                    className="p-2 text-[#3A2A10] hover:text-[#0A0604] hover:bg-[#2B2214]/[0.06] rounded-sm"
                    aria-label="Edit"
                    title="Edit"
                    data-testid={`library-edit-${d.id}`}
                  >
                    <Pencil size={13} />
                  </button>
                  <button
                    onClick={() => onDelete(d)}
                    className="p-2 text-[#3A2A10] hover:text-rose-500 hover:bg-rose-500/10 rounded-sm"
                    aria-label="Delete"
                    title="Delete"
                    data-testid={`library-delete-${d.id}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <LibraryFormSheet
        open={formOpen}
        onOpenChange={(o) => { setFormOpen(o); if (!o) setEditing(null); }}
        doc={editing}
        categories={cats}
        validation={validation}
        existingCollections={allCollections.map((c) => c.name)}
        onSaved={() => {
          setFormOpen(false);
          setEditing(null);
          load();
          // Refetch collections so any newly-added collection name appears
          // in the sidebar without requiring a full page reload.
          loadCollections();
          fetchLibraryCategories().then((d) => setCats(d.categories || [])).catch(() => {});
        }}
      />
        </div>
      </div>
    </div>
  );
}
